import { createAsyncThunk, createSlice, miniSerializeError, type SerializedError } from '@reduxjs/toolkit';
import _mapKeys from 'lodash/mapKeys';
import _toUpper from 'lodash/toUpper';
import authService from 'src/services/authService';
import featureFlagService, { type FeatureFlagsPayload } from 'src/services/featureFlagService';
import apiClient from 'src/utils/apiClient';
import { appendError } from 'src/utils/helpers';

import { LoadingStates } from '../constants';

// **** Constants **** //

const ERROR_MESSAGE_PREPENDS = {
  Update: 'Failed to update feature flag:',
} as const;

// Keeps the statically typed flags defined before the API responds, and if it omits one.
const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  PLACEMENT_TRANSFER: false,
  DECISION_PORTAL: false,
  JOB_MODIFICATIONS: false,
  CORRESPONDENCE: false,
  PERSONA: false,
  TASK_GROUPING: false,
  T2_PLUS_EXPERIENCE: false,
  NEW_MODIFIED_DUTY_EXPERIENCE: false,
};

// **** Types **** //

/**
 * Flags that frontend logic branches on. Add a flag here only when the frontend reads it.
 * State also holds any back-end only flags the API returns, which only the feature flags
 * page reads, so keeping this type closed means a misspelled flag is still a compile error.
 */
export type FeatureFlags = {
  PLACEMENT_TRANSFER: boolean;
  DECISION_PORTAL: boolean;
  JOB_MODIFICATIONS: boolean;
  CORRESPONDENCE: boolean;
  PERSONA: boolean;
  TASK_GROUPING: boolean;
  T2_PLUS_EXPERIENCE: boolean;
  NEW_MODIFIED_DUTY_EXPERIENCE: boolean;
};

export type UserState = {
  token: string;
  alias: string;
  departmentCode: string;
  lastName: string;
  firstName: string;
  email: string;
  ldap: string[];
  siteLocation: string;
  preferredSite: number;
  userLoading: LoadingStates;
  authError: SerializedError | null;
  jobLevel: string;
  jobDescription: string;
  hasPermission: boolean;
  featureFlags: FeatureFlags;
  pageLastLoadTimestamp: number;
  isLoading: boolean;
  isFeatureFlagsLoaded: boolean;
  isLoadingUpdateFeatureFlag: boolean;
  updateFeatureFlagErrorMessage: string;
};

interface UpdateFeatureFlagPayload {
  featureName: string;
  isEnabled: boolean;
}

// **** Functions **** //

/**
 * Map an API payload onto state, upper-casing each flag name. Unrecognized flags are kept so
 * the feature flags page can list them without a frontend change.
 */
const parseFeatureFlags = (payload: FeatureFlagsPayload): FeatureFlags => ({
  ...DEFAULT_FEATURE_FLAGS,
  ..._mapKeys(payload, (_isEnabled, featureName) => _toUpper(featureName)),
});

/**
 * Get the authenticated user
 */
export const getUser = createAsyncThunk('user/getUser', async () => {
  return authService.login(apiClient);
});

/**
 * Fetch the list of feature flags (from the back-end environment properties)
 */
export const getFeatureFlags = createAsyncThunk<FeatureFlagsPayload>('user/getFeatureFlags', async () => {
  return featureFlagService.getFeatureFlags();
});

/**
 * Update in the back-end whether a particular feature-flag is enable.
 */
export const updateFeatureFlag = createAsyncThunk('user/updateFeatureFlag', (payload: UpdateFeatureFlagPayload) => {
  const { featureName, isEnabled } = payload;
  return featureFlagService.updateFeatureFlag(featureName, isEnabled);
});

// **** Setup **** //

const initialState: UserState = {
  token: '',
  alias: '',
  departmentCode: '',
  lastName: '',
  firstName: '',
  email: '',
  ldap: [],
  siteLocation: '',
  preferredSite: 0,
  userLoading: LoadingStates.Pending,
  authError: null,
  jobLevel: '',
  jobDescription: '',
  hasPermission: true,
  featureFlags: DEFAULT_FEATURE_FLAGS,
  pageLastLoadTimestamp: 0,
  isLoading: false,
  isFeatureFlagsLoaded: false,
  isLoadingUpdateFeatureFlag: false,
  updateFeatureFlagErrorMessage: '',
};

// UserSlice
const { reducer, actions } = createSlice({
  name: 'user',
  initialState,
  reducers: {
    getUserFailure: (state) => {
      state.userLoading = LoadingStates.Rejected;
    },
    setPreferredSite: (state, action) => {
      state.preferredSite = action.payload;
    },
    /**
     * @testOnly functions
     */
    setLdap: (state, action) => {
      state.ldap = action.payload;
    },
    setEnvLoaded: (state) => {
      state.userLoading = LoadingStates.Fulfilled;
      state.isFeatureFlagsLoaded = true;
    },
  },
  extraReducers: (builder) => {
    // ** Fetch "Feature Flags" ** //
    builder.addCase(getFeatureFlags.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getFeatureFlags.fulfilled, (state, { payload }: { payload: FeatureFlagsPayload }) => {
      state.isLoading = false;
      state.featureFlags = parseFeatureFlags(payload);
      state.isFeatureFlagsLoaded = true;
    });
    builder.addCase(getFeatureFlags.rejected, (state) => {
      state.isLoading = false;
      state.isFeatureFlagsLoaded = true;
    });
    // ** Update "Feature Flag" ** //
    builder.addCase(updateFeatureFlag.pending, (state) => {
      state.isLoadingUpdateFeatureFlag = true;
    });
    builder.addCase(updateFeatureFlag.fulfilled, (state, { payload }: { payload: FeatureFlagsPayload }) => {
      state.isLoadingUpdateFeatureFlag = false;
      state.featureFlags = parseFeatureFlags(payload);
      state.updateFeatureFlagErrorMessage = '';
    });
    builder.addCase(updateFeatureFlag.rejected, (state, { error }) => {
      state.isLoadingUpdateFeatureFlag = false;
      state.updateFeatureFlagErrorMessage = appendError(ERROR_MESSAGE_PREPENDS.Update, error);
    });
    // ** Fetch "User" ** //
    builder.addCase(getUser.pending, (state) => {
      state.userLoading = LoadingStates.Pending;
    });
    builder.addCase(getUser.fulfilled, (state, { payload }) => {
      const { alias, token, email, firstName, lastName, siteLocation, ldap, departmentCode, jobLevel, jobDescription } =
        payload;
      state.token = token;
      state.alias = alias;
      state.email = email;
      state.firstName = firstName;
      state.lastName = lastName;
      state.siteLocation = siteLocation;
      state.ldap = ldap;
      state.userLoading = LoadingStates.Fulfilled;
      state.departmentCode = departmentCode;
      state.jobLevel = jobLevel;
      state.jobDescription = jobDescription;
      state.pageLastLoadTimestamp = Date.now();
      if (state.ldap.length === 0) {
        state.hasPermission = false;
      }
      // TODO: add JWT token to api header
    });
    builder.addCase(getUser.rejected, (state, { error }) => {
      state.authError = miniSerializeError(error);
      state.userLoading = LoadingStates.Rejected;
      state.hasPermission = false;
    });
  },
});

// **** Export **** //

export const { setPreferredSite, setLdap, setEnvLoaded } = actions;
export default reducer;
